/**
 * The four subscription tiers -- one definition, two consumers.
 *
 * This lived inside PricingPage.vue until 2026-08-26, when the report's
 * end-of-report banner needed to show the same plans. Copying the array into a
 * second component would have guaranteed drift: prices, audit counts and
 * feature lists change, and the copy nobody remembers to edit is the one the
 * customer reads. So it moved here verbatim instead.
 *
 * Anything that quotes a price or a limit to a user reads from this file. If a
 * third surface needs it, import it -- do not paste it.
 *
 * Shape notes for new consumers:
 *   monthly / yearly   dollars per month; `yearly` is the discounted monthly
 *                      rate when billed annually, not the annual total
 *   customPrice        true means "no fixed price" -- render the tag, not a number
 *   highlights         short scannable list, for cards
 *   features           full list including included:false rows, for the table
 */

export const TIERS = [
  // ── TIER 1: STARTER ───────────────────────────────────────────
  {
    key: 'starter',
    name: 'Starter',
    tag: 'Find your footing',
    desc: 'A taste of SEO intelligence. Five full audits — no credit card, no expiration.',
    monthly: 0,
    yearly: 0,
    featured: false,
    customPrice: false,
    cta: { label: 'Start for free', to: '/register' },
    highlights: [
      // Starter cap raised 3 -> 5 on 2026-09-15 (owner decision); the lifetime
      // allotment is enforced in src/services/audit_budget.py (TIER_LIMITS),
      // which is the SSOT -- keep this copy in step when either changes.
      '5 full audits, total',
      'All 17 analysis modules',
      'Core Web Vitals (mobile + desktop)',
      'PDF + CSV + JSON export',
      '7-day history',
      'Community support',
    ],
    features: [
      { label: '5 full audits, total', included: true },
      { label: 'All 17 analysis modules', included: true },
      { label: 'Core Web Vitals (mobile + desktop)', included: true },
      { label: 'Up to 3 competitors per audit', included: true },
      { label: 'PDF + CSV + JSON export', included: true },
      { label: '7-day history', included: true },
      { label: 'SEO Assistant (10 msgs/mo)', included: true, tooltip: 'The AI chatbot, grounded in your report data — capped at 10 messages a month on Starter. Unlimited on paid plans.' },
      { label: 'Auto-competitor discovery', included: false },
      { label: '3 lead credits to try prospecting', included: true, tooltip: 'Each credit = one company analyzed, scored, and ready to pitch. Free to try: 3 companies, no card. Upgrade for 20/month (Pro) or 50/month (Agency).' },
      { label: 'White-label reports', included: false },
    ],
  },

  // ── TIER 2: PRO ──────────────────────────────────────────────
  {
    key: 'professional',
    name: 'Pro',
    tag: 'For freelancers',
    desc: '30 audits, an unlimited SEO Assistant, and prospecting — everything a serious SEO practitioner needs.',
    monthly: 49,
    yearly: 39,
    featured: true,
    customPrice: false,
    cta: { label: 'Start 14-day trial', to: '/register' },
    highlights: [
      '30 full audits / month',
      '20 lead credits / month',
      'SEO Assistant (unlimited)',
      'Auto-competitor discovery (5/mo)',
      'GSC & GA4 live data',
      'ROI projections (traffic + revenue)',
      'Priority email support',
    ],
    features: [
      { label: '30 full audits / month', included: true },
      { label: 'All 17 analysis modules', included: true },
      { label: 'Core Web Vitals (mobile + desktop)', included: true },
      { label: 'Up to 3 competitors per audit', included: true },
      { label: 'PDF + CSV + JSON export', included: true },
      { label: 'Unlimited history', included: true },
      { label: 'SEO Assistant (unlimited)', included: true, tooltip: 'Grounded on your report data. Ask why a score moved, get fixes ranked by impact.' },
      { label: 'Auto-competitor discovery (5/mo)', included: true, tooltip: 'Automatically finds your top competitors and analyzes keyword gaps.' },
      { label: '20 lead credits / month', included: true, tooltip: 'Each credit = one company analyzed, scored, and ready to pitch. Average user lands their first client within 20 credits. Credit packs available if you need more.' },
      { label: 'GSC & GA4 live data', included: true, tooltip: 'Connect Search Console + Analytics. Real traffic, clicks, and conversions — not estimates.' },
      { label: 'ROI projections (traffic + revenue)', included: true, tooltip: 'Every report shows estimated dollar impact of fixing SEO issues.' },
      { label: 'White-label reports', included: false },
    ],
  },

  // ── TIER 3: AGENCY ───────────────────────────────────────────
  {
    key: 'agency',
    name: 'Agency',
    tag: 'For agencies & teams',
    desc: 'White-label everything you send to clients — your own domain, your own SMTP, scheduled re-scans on autopilot. Run your entire SEO practice on one platform.',
    monthly: 199,
    yearly: 159,
    featured: false,
    customPrice: false,
    cta: { label: 'Start 14-day trial', to: '/register' },
    highlights: [
      '60 full audits / month',
      'White-label reports & custom domain',
      '50 lead credits / month',
      'Everything in Pro',
      'Auto-competitor discovery (10/mo)',
      'GSC & GA4 live data',
      'Priority email support',
    ],
    features: [
      { label: '60 full audits / month', included: true },
      { label: 'Everything in Pro', included: true },
      { label: 'Up to 3 competitors per audit', included: true },
      { label: 'Auto-competitor discovery (10/mo)', included: true },
      { label: '50 lead credits / month', included: true, tooltip: 'Each credit = one company analyzed, scored, and ready to pitch. Average user lands their first client within 20 credits. Built for an active agency pipeline — credit packs available if you need more.' },
      { label: 'GSC & GA4 live data', included: true, tooltip: 'Connect Search Console + Analytics. Real traffic, clicks, and conversions — not estimates.' },
      { label: 'White-label reports & custom domain', included: true, tooltip: 'Serve reports from your own domain with your logo, colors, and typography.' },
      { label: 'Priority email support', included: true },
    ],
  },

  // ── TIER 4: ENTERPRISE ────────────────────────────────────────
  {
    key: 'enterprise',
    name: 'Enterprise',
    tag: 'For large orgs & platforms',
    desc: 'Custom volume, dedicated support, legal-ready. Built for security reviews and procurement cycles — every deal scoped to your needs.',
    monthly: 0,
    yearly: 0,
    featured: false,
    customPrice: true,
    cta: { label: 'Contact sales', to: '/contact-sales' },
    highlights: [
      'Custom audit & lead volume',
      'Everything in Agency',
      'Unlimited competitors per audit',
      'Auto-competitor discovery (25/mo)',
      'GSC & GA4 live data',
      'Custom contract & DPA (legal)',
      'Dedicated support',
    ],
    features: [
      { label: 'Custom audit volume', included: true },
      { label: 'Everything in Agency', included: true },
      { label: 'Unlimited competitors per audit', included: true },
      { label: 'Auto-competitor discovery (25/mo)', included: true },
      { label: 'Custom lead volume', included: true, tooltip: 'Volume scoped to your needs — negotiated per contract. No arbitrary caps.' },
      { label: 'GSC & GA4 live data', included: true, tooltip: 'Connect Search Console + Analytics. Real traffic, clicks, and conversions — not estimates.' },
      { label: 'Custom contract & DPA', included: true },
      { label: 'Dedicated support', included: true },
    ],
  },
];


/**
 * The paid plans, in upgrade order -- what a Starter user can move up to.
 *
 * Derived, not a second list: it filters TIERS, so a price or feature edit
 * above reaches every upsell surface automatically.
 */
export const PAID_TIERS = TIERS.filter((t) => t.key !== 'starter');

/**
 * Tier values that carry no paid entitlements.
 *
 * Both spellings are real: the DB column defaults to 'starter', but older rows
 * and unauthenticated/guest reads surface 'free', null or ''. An upgrade prompt
 * that missed any of them would silently never appear for those users.
 */
export const FREE_TIER_KEYS = ['starter', 'free', ''];

/** True when this subscription tier should be offered an upgrade. */
export function isFreeTier(tier) {
  if (tier === null || tier === undefined) return true;
  return FREE_TIER_KEYS.includes(String(tier).trim().toLowerCase());
}

/**
 * Stripe keys the paid tiers under different names than the UI does
 * (professional -> growth, agency -> scale). Mirrors stripe_endpoints.py's
 * _TIER_NORMALIZE; the Stripe name is what comes back on the checkout
 * success_url, so it has to be mapped back before pricing can be looked up.
 */
const STRIPE_TIER_ALIASES = { growth: 'professional', scale: 'agency' };

/**
 * The amount actually billed for a tier/billing pair, in USD.
 *
 * This is what a GA4 `purchase` event should carry as `value` -- without it
 * GA4 counts the conversion but reports $0 revenue, because a purchase with
 * no value has nothing to attribute.
 *
 * IMPORTANT: `yearly` in TIERS is the discounted MONTHLY rate, not the annual
 * total, so an annual charge is 12x that (39/mo -> 468/yr).
 *
 * Returns null when there is no fixed amount to report (free tiers and
 * custom/negotiated Enterprise pricing) -- callers must omit `value` rather
 * than send 0, which would be recorded as a real zero-revenue purchase.
 */
export function chargedAmountFor(tierKey, billing = 'monthly') {
  const key = STRIPE_TIER_ALIASES[String(tierKey || '').toLowerCase()] || String(tierKey || '').toLowerCase();
  const tier = TIERS.find((t) => t.key === key);
  if (!tier || tier.customPrice) return null;
  const amount = billing === 'yearly' ? (tier.yearly || 0) * 12 : (tier.monthly || 0);
  return amount > 0 ? amount : null;
}
